import { useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { useQueryClient } from '@tanstack/react-query'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderActions } from '@/components/navigation/page-header-actions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { OrganisationProfileDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { Briefcase, Loader2 } from 'lucide-react'
import { useOrganisations, useManagedOrganisationDetail } from '@/hooks/use-organisations'
import { useOrganisationOpportunities } from '@/hooks/use-organisation-opportunities'
import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { OrganisationDetailsCanvas } from '@/components/organisation-details-canvas'
import { OrganisationAboutCanvas } from '@/components/organisation/organisation-about-canvas'
import { OrganisationLocationsCanvas } from '@/components/organisation/organisation-locations-canvas'
import { OrganisationLocationsSection } from '@/components/organisation/organisation-locations-section'
import { OrganisationBannerCanvas } from '@/components/organisation/organisation-banner-canvas'
import { OrganisationBannerEmptySection, OrganisationBannerSection } from '@/components/organisation/organisation-banner-section'
import { PublicShareLinkActions } from '@/components/public/public-share-link-actions'
import { employerOrganisationHomePath, isCoverBannerEnabled } from '@/constants'
import { isPublicOrganisationsEnabled } from '@/utils/candidate-organisation-path'
import { publicOrganisationUrl } from '@/utils/public-share-url'
import { PublicContactStrip } from '@/components/ui/entity-contact-sections'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { EntityLogo } from '@/components/ui/entity-logo'
import { EntityAboutSection } from '@/components/ui/entity-about-section'
import { EntityOrganisationFactsBlock } from '@/components/entity-organisation-facts-block'
import {
  EntityIdentityCard,
  EntityIdentityCardBody,
  EntityIdentityCardMedia,
  EntityIdentityCardMetadata,
  EntityIdentityCardTitle,
} from '@/components/entity-identity-card'

export interface OrganisationProfileViewProps {
  /** Omit for the pre-create empty profile (hub with no organisations). */
  slug?: string
  /** Opens create canvas when the profile has no organisation yet. */
  onCreate?: () => void
}

export function OrganisationProfileView({ slug: slugProp, onCreate }: OrganisationProfileViewProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { isUploadingOrganisationBanner } = useOrganisations()

  const slug = slugProp?.trim() || undefined
  const isEmpty = !slug
  const homePath = employerOrganisationHomePath()

  const { data: organisation, isLoading, error } = useManagedOrganisationDetail(slug)

  const {
    opportunities: managedOpportunities,
    isLoading: opportunitiesLoading,
    error: opportunitiesError,
  } = useOrganisationOpportunities(slug ?? '')

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const handleOpenOrganisationCoverCanvas = () => {
    if (!organisation || !slug) return

    openCanvas({
      title: 'Cover image',
      preventAutoFocus: true,
      content: <OrganisationBannerCanvas slug={slug} organisation={organisation} onClose={closeCanvas} />,
    })
  }

  const handleOpenOrganisationDetailsCanvas = () => {
    if (!organisation || !slug) return

    openCanvas({
      title: 'Edit organisation details',
      preventAutoFocus: true,
      content: (
        <OrganisationDetailsCanvas
          slug={slug}
          variant="profile"
          onClose={closeCanvas}
          onSaved={(updated) => {
            if (updated.slug !== slug) {
              queryClient.removeQueries({ queryKey: ['organisation', slug] })
              queryClient.setQueryData(['organisation', updated.slug], updated)
              navigateWithThreadPreservation(
                navigate,
                `/employer/organisation/${updated.slug}`,
                searchParams,
              )
            } else {
              queryClient.setQueryData(['organisation', slug], updated)
            }
          }}
        />
      ),
    })
  }

  const handleOpenOrganisationLocationsCanvas = useCallback(() => {
    if (!organisation || !slug) return

    openCanvas({
      title: 'Locations',
      preventAutoFocus: true,
      content: (
        <OrganisationLocationsCanvas
          slug={slug}
          organisation={organisation}
          onClose={closeCanvas}
          onSaved={(updated) => {
            queryClient.setQueryData(['organisation', slug], updated)
          }}
        />
      ),
    })
  }, [organisation, slug, openCanvas, closeCanvas, queryClient])

  const handleOpenOrganisationAboutCanvas = useCallback(() => {
    if (!organisation || !slug) return

    openCanvas({
      title: 'About',
      preventAutoFocus: true,
      content: (
        <OrganisationAboutCanvas
          slug={slug}
          organisation={organisation}
          onClose={closeCanvas}
          onSaved={(updated) => {
            if (updated.slug !== slug) {
              queryClient.removeQueries({ queryKey: ['organisation', slug] })
              queryClient.setQueryData(['organisation', updated.slug], updated)
              navigateWithThreadPreservation(
                navigate,
                `/employer/organisation/${updated.slug}`,
                searchParams,
              )
            } else {
              queryClient.setQueryData(['organisation', slug], updated)
            }
          }}
        />
      ),
    })
  }, [organisation, slug, openCanvas, closeCanvas, queryClient, navigate, searchParams])

  const handleEditProfile = () => {
    if (isEmpty) {
      onCreate?.()
      return
    }
    handleOpenOrganisationDetailsCanvas()
  }

  const handleEditCover = () => {
    if (isEmpty) {
      onCreate?.()
      return
    }
    handleOpenOrganisationCoverCanvas()
  }

  if (!isEmpty && isLoading) {
    return <OrganisationProfileDetailSkeleton />
  }

  if (!isEmpty && (error || !organisation)) {
    return (
      <PageContainer>
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Organisation Not Found</h1>
          <p className="mb-6 text-muted-foreground">
            The organisation you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.
          </p>
          <Button onClick={() => navigateWithThreadPreservation(navigate, homePath, searchParams)}>
            Back to organisation
          </Button>
        </div>
      </PageContainer>
    )
  }

  const coverEnabled = isCoverBannerEnabled('organisation')
  const publicOrganisationsEnabled = isPublicOrganisationsEnabled()
  const organisationPublicUrl =
    publicOrganisationsEnabled && organisation?.slug ? publicOrganisationUrl(organisation.slug) : ''
  const publicShareActions = publicOrganisationsEnabled ? (
    <PublicShareLinkActions url={organisationPublicUrl} iconOnly />
  ) : null

  const jobCount = isEmpty ? 0 : opportunitiesLoading ? null : opportunitiesError ? '—' : managedOpportunities.length

  return (
    <PageContainer>
      {publicOrganisationsEnabled ? (
        <PageHeader
          leading={<PageHeaderLeading />}
          actions={
            publicShareActions ? (
              <PageHeaderActions tertiary={publicShareActions} />
            ) : undefined
          }
        />
      ) : null}

      {coverEnabled ? (
        isEmpty || !organisation ? (
          <OrganisationBannerEmptySection onEditClick={handleEditCover} />
        ) : (
          <OrganisationBannerSection
            organisation={organisation}
            bannerUrl={organisation.banner_url ?? null}
            isUploadingBanner={isUploadingOrganisationBanner}
            onEditClick={handleEditCover}
          />
        )
      ) : null}

      <EntityIdentityCard
        coverEnabled={coverEnabled}
        media={
          <EntityIdentityCardMedia
            variant="logo"
            agentHighlight={organisation?.id ? `organisation:${organisation.id}:logo` : undefined}
          >
            <EntityLogo
              src={organisation?.logo_url}
              alt={organisation?.name ? `${organisation.name} logo` : 'Organisation logo'}
              size="hero"
              framed
            />
          </EntityIdentityCardMedia>
        }
        onEdit={handleEditProfile}
        editAriaLabel={isEmpty ? 'Create organisation' : 'Edit organisation details'}
        editTitle={isEmpty ? 'Create organisation' : 'Edit organisation details'}
        editShowOnHover={!isEmpty}
      >
        <EntityIdentityCardBody>
          {organisation?.name?.trim() ? (
            <EntityIdentityCardTitle>{organisation.name}</EntityIdentityCardTitle>
          ) : null}
          {organisation ? (
            <EntityIdentityCardMetadata>
              <EntityOrganisationFactsBlock
                industry={organisation.industry}
                company_size={organisation.company_size}
                founded_year={organisation.founded_year}
                city={organisation.city}
                country={organisation.country}
                locations={organisation.locations}
              />
              <PublicContactStrip
                website={organisation.website}
                linkedin_url={organisation.linkedin_url}
                x_url={organisation.x_url}
                {...extraSocialContactFrom(organisation)}
              />
            </EntityIdentityCardMetadata>
          ) : null}
        </EntityIdentityCardBody>
      </EntityIdentityCard>

      <EntityAboutSection
        body={organisation?.description}
        emptyVariant="cta"
        emptyCtaLabel="Add about"
        onEdit={isEmpty ? handleEditProfile : handleOpenOrganisationAboutCanvas}
        editAriaLabel={isEmpty ? 'Create organisation' : 'Edit about'}
        editTitle={isEmpty ? 'Create organisation' : 'Edit about'}
        agentHighlight={organisation?.id ? `organisation:${organisation.id}:about` : undefined}
      />

      <OrganisationLocationsSection
        locations={organisation?.locations}
        onEdit={isEmpty ? handleEditProfile : handleOpenOrganisationLocationsCanvas}
        emptyCtaLabel="Add a location"
        agentHighlight={organisation?.id ? `organisation:${organisation.id}:locations` : undefined}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Job opportunities
          </CardTitle>
          <CardDescription>Manage job postings for this organisation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-h-9 space-y-0.5">
              <p className="text-2xl font-bold" aria-busy={!isEmpty && opportunitiesLoading}>
                {!isEmpty && opportunitiesLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading count" />
                ) : (
                  jobCount
                )}
              </p>
              {!isEmpty && opportunitiesError ? (
                <p className="text-xs text-destructive">
                  Couldn&apos;t load count
                  {opportunitiesError instanceof Error ? `: ${opportunitiesError.message}` : ''}.
                </p>
              ) : null}
            </div>
            <Button
              variant="outline"
              disabled={isEmpty || !organisation?.slug}
              onClick={() => {
                if (!organisation?.slug) return
                navigateWithThreadPreservation(
                  navigate,
                  `/employer/organisations/${organisation.slug}/jobs`,
                  searchParams,
                )
              }}
            >
              Manage jobs
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  )
}

export function OrganisationProfile() {
  const { slug } = useParams<{ slug: string }>()
  const trimmed = slug?.trim()
  if (!trimmed) {
    return (
      <PageContainer>
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Organisation Not Found</h1>
          <p className="text-muted-foreground">Missing organisation slug in the URL.</p>
        </div>
      </PageContainer>
    )
  }
  return <OrganisationProfileView slug={trimmed} />
}
